(() => {
  // Shared helpers (scope-safe within IIFE)
  function announce(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.classList && el.classList.remove('visually-hidden');
  }
  function toNumericId(id) {
    if (typeof id === 'number') return id;
    const s = String(id);
    const m = /\/(\d+)$/.exec(s);
    return m ? Number(m[1]) : Number(s);
  }
  async function addItems(items) {
    // Shopify AJAX Cart: /cart/add.js accepts items array
    // https://shopify.dev/docs/api/ajax/reference/cart#post-cart-add-js
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items })
    });
    if (!res.ok) {
      const t = await res.text().catch(()=>'');
      throw new Error(t || 'Add failed');
    }
    return res.json();
  }
  function buildMap(mapScriptEl) {
    let raw = {};
    try { raw = JSON.parse(mapScriptEl?.textContent || '{}'); } catch(e) { raw = {}; }
    const out = {};
    for (const [k,v] of Object.entries(raw)) out[String(k).toUpperCase()] = v;
    return out;
  }

  // ---------- Single-line section (#quick-sku-add) ----------
  const singleRoot = document.getElementById('quick-sku-add');
  if (singleRoot) {
    const skuEl = singleRoot.querySelector('#qsa-sku');
    const qtyEl = singleRoot.querySelector('#qsa-qty');
    const addBtn = singleRoot.querySelector('#qsa-add');
    const status = singleRoot.querySelector('#qsa-status');
    const mapEl = singleRoot.querySelector('#qo-inline-sku-map');
    const SKUMAP = buildMap(mapEl);

    async function handleAdd() {
      let sku = (skuEl.value || '').trim();
      if (!sku) { announce(status, 'Enter a SKU.'); skuEl.focus(); return; }
      let qty = parseInt(qtyEl.value, 10);
      if (!Number.isFinite(qty) || qty < 1) qty = 1;
      if (qty > 999) qty = 999;

      const rec = SKUMAP[sku.toUpperCase()];
      if (!rec || !rec.id) { announce(status, 'SKU not found.'); return; }

      const idNum = toNumericId(rec.id);
      try {
        addBtn.disabled = true;
        await addItems([{ id: idNum, quantity: qty }]);
        announce(status, `Added ${sku} × ${qty}.`);
        if (singleRoot.dataset.redirect === 'true') window.location.href = '/cart';
        document.dispatchEvent(new CustomEvent('quicksku:added', { detail: { sku, qty, id: idNum } }));
        skuEl.value = ''; qtyEl.value = '1'; skuEl.focus();
      } catch (e) {
        announce(status, 'Add failed: ' + (e.message || 'Error'));
      } finally {
        addBtn.disabled = false;
      }
    }
    addBtn.addEventListener('click', handleAdd);
    skuEl.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }});
    qtyEl.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }});
  }

  // ---------- Inline snippet (#quick-sku-inline) ----------
  const inlineRoot = document.getElementById('quick-sku-inline');
  if (inlineRoot) {
    const skuEl = inlineRoot.querySelector('#qsa-sku');
    const qtyEl = inlineRoot.querySelector('#qsa-qty');
    const addBtn = inlineRoot.querySelector('#qsa-add');
    const status = inlineRoot.querySelector('#qsa-status');
    const mapEl = inlineRoot.querySelector('#qo-inline-sku-map');
    const SKUMAP = buildMap(mapEl);

    async function handleAdd() {
      let sku = (skuEl.value || '').trim();
      if (!sku) { announce(status, 'Enter a SKU.'); skuEl.focus(); return; }
      let qty = parseInt(qtyEl.value, 10);
      if (!Number.isFinite(qty) || qty < 1) qty = 1;
      if (qty > 999) qty = 999;

      const rec = SKUMAP[sku.toUpperCase()];
      if (!rec || !rec.id) { announce(status, 'SKU not found.'); return; }

      const idNum = toNumericId(rec.id);
      try {
        addBtn.disabled = true;
        await addItems([{ id: idNum, quantity: qty }]);
        announce(status, `Added ${sku} × ${qty}.`);
        skuEl.value = ''; qtyEl.value = '1'; skuEl.focus();
      } catch (e) { announce(status, 'Add failed: ' + (e.message || 'Error')); }
      finally { addBtn.disabled = false; }
    }
    addBtn.addEventListener('click', handleAdd);
    skuEl.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }});
    qtyEl.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }});
  }

  // ---------- Multi-line page (#quick-sku-add-multi) ----------
  const multiRoot = document.getElementById('quick-sku-add-multi');
  if (multiRoot) {
    const tbody = multiRoot.querySelector('#qsaM-tbody');
    const addRowsBtn = multiRoot.querySelector('#qsaM-add-rows');
    const submitBtn = multiRoot.querySelector('#qsaM-submit');
    const clearBtn = multiRoot.querySelector('#qsaM-clear');
    const status = multiRoot.querySelector('#qsaM-status');
    const mapEl = multiRoot.querySelector('#qo-inline-sku-map');
    const addStep = parseInt(multiRoot.dataset.addstep || '5', 10) || 5;
    const redirect = multiRoot.dataset.redirect === 'true';
    const SKUMAP = buildMap(mapEl);

    function addRows(n) {
      const current = tbody.querySelectorAll('tr').length;
      for (let i = 0; i < n; i++) {
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td class="qsaM-idx">${current + i + 1}</td>
          <td><input type="text" class="qsaM-sku" inputmode="text" autocomplete="off" placeholder="SKU${current+i+1}"></td>
          <td><input type="number" class="qsaM-qty" min="1" max="999" step="1" value="1"></td>
          <td class="qsaM-state"></td>
        `;
        tbody.appendChild(tr);
      }
    }

    addRowsBtn.addEventListener('click', () => addRows(addStep));
    clearBtn.addEventListener('click', () => {
      tbody.innerHTML = '';
      addRows(5);
      announce(status, 'Cleared.');
    });

    function normalizeQty(v) {
      let q = parseInt(v, 10);
      if (!Number.isFinite(q) || q < 1) q = 1;
      if (q > 999) q = 999;
      return q;
    }
    function rowSetState(tr, msg, ok) {
      const cell = tr.querySelector('.qsaM-state');
      cell.textContent = msg || '';
      tr.classList.remove('qsaM-row-ok', 'qsaM-row-err');
      if (ok === true) tr.classList.add('qsaM-row-ok');
      else if (ok === false) tr.classList.add('qsaM-row-err');
    }

    async function handleSubmitAll() {
      const rows = Array.from(tbody.querySelectorAll('tr'));
      const items = [];
      let errs = 0, oks = 0;

      // Build items & validate
      for (const tr of rows) {
        const sku = (tr.querySelector('.qsaM-sku')?.value || '').trim();
        const qty = normalizeQty(tr.querySelector('.qsaM-qty')?.value || '1');
        if (!sku) { rowSetState(tr, '', null); continue; } // blank row is ignored
        const rec = SKUMAP[sku.toUpperCase()];
        if (!rec || !rec.id) { rowSetState(tr, 'SKU not found', false); errs++; continue; }
        const idNum = toNumericId(rec.id);
        if (!Number.isFinite(idNum)) { rowSetState(tr, 'Invalid variant ID', false); errs++; continue; }
        items.push({ id: idNum, quantity: qty, _tr: tr, _sku: sku });
      }

      if (!items.length) { announce(status, 'No valid lines to add.'); return; }

      // Batch in groups of 20 (Ajax Cart best practice)
      let i = 0;
      announce(status, `Adding ${items.length} line(s)…`);
      while (i < items.length) {
        const batch = items.slice(i, i + 20);
        try {
          await addItems(batch.map(b => ({ id: b.id, quantity: b.quantity })));
          batch.forEach(b => { rowSetState(b._tr, 'Added', true); oks++; });
          i += batch.length;
          announce(status, `Added ${oks}/${items.length}. Errors: ${errs}.`);
        } catch (e) {
          // mark this batch as failed and continue
          batch.forEach(b => { rowSetState(b._tr, 'Add failed', false); errs++; });
          i += batch.length;
          announce(status, `Some items failed to add. Progress ${oks + batch.length}/${items.length}.`);
        }
      }

      if (redirect) window.location.href = '/cart';
    }

    submitBtn.addEventListener('click', handleSubmitAll);
  }
})();
